let audioEl: HTMLAudioElement | null = null;
let unlocked = false;
let playSeq = 0;
let activeObjectUrls: string[] = [];

// tiny silent wav (very small) used to "unlock" audio on iOS user gesture
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=';

function getAudioEl() {
  if (audioEl) return audioEl;

  audioEl = new Audio();
  // @ts-ignore - playsInline exists on iOS Safari but not always in TS libs
  audioEl.playsInline = true;
  audioEl.preload = 'auto';
  return audioEl;
}

async function tryPlay(el: HTMLAudioElement) {
  const p = el.play();
  if (p && typeof (p as any).catch === 'function') {
    await p;
  }
}

async function unlock() {
  if (unlocked) return;
  const el = getAudioEl();

  // Must be called from a user gesture (e.g. button click).
  el.src = SILENT_WAV;
  el.loop = false;
  // iOS Safari: using muted=true sometimes doesn't unlock future unmuted playback.
  // Use volume=0 instead (still inaudible, but counts as user-initiated playback).
  el.muted = false;
  el.volume = 0;

  try {
    // iOS Safari can leave play() pending forever; bound the unlock attempt.
    await Promise.race([
      tryPlay(el),
      new Promise((resolve) => setTimeout(resolve, 900))
    ]);
    el.pause();
    el.currentTime = 0;
    // If we got here without throwing, consider audio "unlocked" for subsequent plays.
    unlocked = true;
  } catch (_) {
    // If this fails, iOS hasn't granted gesture context; caller should retry on next tap.
  } finally {
    el.volume = 1;
  }
}

function buildTtsUrl(text: string, token: string, brazilian: boolean) {
  const api = process.env.REACT_APP_API_URL;
  const qText = encodeURIComponent(text);
  const qAuth = encodeURIComponent(token);
  const qBr = brazilian ? 'true' : 'false';
  const ts = Date.now();
  return `${api}/api/v1/tts?text=${qText}&authorization=${qAuth}&brazilian=${qBr}&ts=${ts}`;
}

function cleanupObjectUrls() {
  const urls = activeObjectUrls;
  activeObjectUrls = [];
  urls.forEach((u) => {
    try {
      URL.revokeObjectURL(u);
    } catch (_) {}
  });
}

function normalizeText(text: string) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function splitIntoChunks(text: string, firstMax = 140, nextMax = 220) {
  const t = normalizeText(text);
  if (!t) return [];

  // Sentence-ish split (keeps punctuation)
  const sentences = t.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g) || [t];
  const chunks: string[] = [];

  const pushWithWordSplit = (s: string, maxLen: number) => {
    const words = s.split(' ');
    let cur = '';
    for (const w of words) {
      const next = cur ? `${cur} ${w}` : w;
      if (next.length > maxLen && cur) {
        chunks.push(cur);
        cur = w;
      } else {
        cur = next;
      }
    }
    if (cur) chunks.push(cur);
  };

  let cur = '';
  for (const sentence of sentences) {
    const limit = chunks.length === 0 ? firstMax : nextMax;
    const next = cur ? `${cur} ${sentence.trim()}` : sentence.trim();

    if (next.length <= limit) {
      cur = next;
      continue;
    }

    if (cur) {
      chunks.push(cur);
      cur = '';
    }

    // sentence alone too long -> split by words
    if (sentence.trim().length > (chunks.length === 0 ? firstMax : nextMax)) {
      pushWithWordSplit(sentence.trim(), chunks.length === 0 ? firstMax : nextMax);
    } else {
      cur = sentence.trim();
    }
  }
  if (cur) chunks.push(cur);

  return chunks;
}

async function fetchAudioBytes(text: string, token: string, brazilian: boolean) {
  const url = buildTtsUrl(text, token, brazilian);
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`TTS HTTP ${resp.status}: ${resp.statusText}`);
  return await resp.arrayBuffer();
}

function bytesToObjectUrl(bytes: ArrayBuffer) {
  const blob = new Blob([bytes], { type: 'audio/mpeg' });
  const objectUrl = URL.createObjectURL(blob);
  activeObjectUrls.push(objectUrl);
  return objectUrl;
}

async function playElementSrc(el: HTMLAudioElement, src: string, mySeq: number, resolveOnPlaying?: () => void) {
  // Stop previous audio cleanly.
  try {
    el.pause();
    el.currentTime = 0;
  } catch (_) {}

  el.src = src;
  el.load();

  await new Promise<void>((resolve, reject) => {
    let timeoutId: any;
    let resolvedPlaying = false;

    const cleanup = () => {
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const onPlaying = () => {
      if (mySeq !== playSeq) return;
      if (!resolvedPlaying) {
        resolvedPlaying = true;
        if (resolveOnPlaying) resolveOnPlaying();
      }
      // keep waiting for ended
    };

    const onEnded = () => {
      if (mySeq !== playSeq) return;
      cleanup();
      resolve();
    };

    const onError = (e: any) => {
      if (mySeq !== playSeq) return;
      cleanup();
      reject(e);
    };

    el.addEventListener('playing', onPlaying);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);

    const p = el.play();
    if (p && typeof (p as any).catch === 'function') {
      (p as any).catch((err: any) => {
        if (mySeq !== playSeq) return;
        cleanup();
        reject(err);
      });
    }

    timeoutId = setTimeout(() => {
      if (mySeq !== playSeq) return;
      cleanup();
      reject(new Error('TTS audio play timeout'));
    }, 30000);
  });
}

async function play(text: string, token: string, brazilian = false) {
  const el = getAudioEl();
  const mySeq = ++playSeq;

  cleanupObjectUrls();

  const chunks = splitIntoChunks(text);
  if (chunks.length <= 1) {
    // Keep old semantics: resolve when audio starts playing.
    const bytes = await fetchAudioBytes(text, token, brazilian);
    const objectUrl = bytesToObjectUrl(bytes);
    let startedResolve: any;
    const started = new Promise<void>((resolve) => (startedResolve = resolve));
    // fire and forget waiting for ended (we don't block the caller)
    playElementSrc(el, objectUrl, mySeq, startedResolve).finally(() => {
      // cleanup this url after playback finished
      cleanupObjectUrls();
    });
    return await started;
  }

  // Chunked mode: resolve once first chunk starts playing, continue queue in background.
  let startedResolve: any;
  let startedReject: any;
  const started = new Promise<void>((resolve, reject) => {
    startedResolve = resolve;
    startedReject = reject;
  });

  (async () => {
    try {
      // Fetch first chunk audio (small) to reduce time-to-first-sound.
      const firstBytes = await fetchAudioBytes(chunks[0], token, brazilian);
      if (mySeq !== playSeq) return;
      const firstUrl = bytesToObjectUrl(firstBytes);

      // Start first chunk and resolve when it begins.
      const firstPlayPromise = playElementSrc(el, firstUrl, mySeq, startedResolve);

      // Prefetch next chunk while current is playing (lookahead=1)
      let nextBytesPromise: Promise<ArrayBuffer> | null =
        chunks.length > 1 ? fetchAudioBytes(chunks[1], token, brazilian) : null;

      // Wait for first chunk to finish
      await firstPlayPromise;

      // Continue remaining chunks with prefetch pipeline.
      for (let i = 1; i < chunks.length; i++) {
        if (mySeq !== playSeq) return;

        // Use prefetched bytes when available; otherwise fetch now.
        const bytes = nextBytesPromise ? await nextBytesPromise : await fetchAudioBytes(chunks[i], token, brazilian);
        if (mySeq !== playSeq) return;
        const url = bytesToObjectUrl(bytes);

        // Kick off prefetch for the following chunk before we start playing this one.
        nextBytesPromise = i + 1 < chunks.length ? fetchAudioBytes(chunks[i + 1], token, brazilian) : null;

        await playElementSrc(el, url, mySeq);
      }

      cleanupObjectUrls();
    } catch (err) {
      cleanupObjectUrls();
      startedReject(err);
    }
  })();

  return await started;
}

function pause() {
  // invalidate any ongoing chunked playback loops
  playSeq++;
  cleanupObjectUrls();
  const el = getAudioEl();
  try {
    el.pause();
  } catch (_) {}
}

export default { play, pause, unlock };